import { Type } from "class-transformer";
import { computed, observable } from "mobx";
import "reflect-metadata";
import { EntityValidationRules } from "services/validationService";
import { maxValidSince, minValidSince } from "../utils/dateUtils";
import FuelType from "entities/fuelType";
import { IVehicle } from "./types";
import ValidChargeResponse from "./validChargeResponse";
import Vehicle from "./vehicle";

export enum ExemptionState {
  Exempted = "EXEMPTED",
  O = "O",
  C = "C",
  P = "P",
}

export const LicensePlateValidationRules: EntityValidationRules<IVehicle> = {
  licensePlate: {
    hasRequiredLength: { min: 1, max: 10 },
    isLicensePlate: true,
  },
};

export const LicensePlateConfirmationValidationRules: EntityValidationRules<OrderItem> = {
  _licensePlateConfirmation: {
    equalWith: { property: "licensePlate" as const },
    hasRequiredLength: { min: 1, max: 10 },
    isLicensePlate: true,
  },
};

export const ValidSinceValidationRules: EntityValidationRules<OrderItem> = {
  validSince: {
    dateRange: {
      min: minValidSince(),
      max: maxValidSince(),
    },
    required: true,
  },
};

export const ChargeTypeIdValidationRules: EntityValidationRules<OrderItem> = {
  chargeTypeId: {
    required: true,
  },
};

export const CountryIdValidationRules: EntityValidationRules<IVehicle> = {
  countryId: { required: "requiredCountryId" },
};

export const FuelTypeValidationRules: EntityValidationRules<OrderItem> = {
  fuelType: { required: true },
};

export const LicensePlatesValidationRules: EntityValidationRules<OrderItem> = {
  licensePlates: {
    required: true,
    hasRequiredLength: {
      min: 1,
      max: 200,
    },
  },
};

export default class OrderItem implements IVehicle {
  id?: string;

  authCode: string;

  @observable licensePlates: string[] = [];

  @observable licensePlate?: string;

  @observable _licensePlateConfirmation?: string;

  @observable countryId?: string;

  @observable canBeChanged?: boolean;

  @observable fuelType?: FuelType = FuelType.Other;

  @observable authCodes: string[] = [];

  @Type(() => Vehicle)
  @observable
  _vehicle: Vehicle;

  @Type(() => Date)
  @observable
  validSince?: Date;

  @Type(() => Date)
  @observable
  validUntil?: Date;

  @observable
  chargeTypeId?: string;

  @observable
  _registrationState?: ExemptionState;

  @observable
  _licensePlateNumberNotInSystem?: string;

  @observable
  _licensePlateCountryIdNotInSystem?: string;

  @observable
  _duplicities = 0;

  @observable
  _visible = true;

  @Type(() => ValidChargeResponse)
  @observable
  _validCharges: ValidChargeResponse[] = [];

  @observable
  _proposedValidSince?: Date;

  @computed
  get _alreadyPaidSince() {
    if (!this._validCharges.length) {
      return undefined;
    }

    return this._validCharges[0].validSince;
  }

  @computed
  get _alreadyPaidUntil() {
    return this._validCharges.reduce((acc: undefined | Date, item) => {
      if (!item.isOverlapped) {
        return item.validUntil;
      }
      return acc;
    }, undefined);
  }

  @computed
  get _licensePlateNotInSystem() {
    return (
      this._licensePlateNumberNotInSystem &&
      this._licensePlateNumberNotInSystem === this.licensePlate &&
      this._licensePlateCountryIdNotInSystem &&
      this._licensePlateCountryIdNotInSystem === this.countryId
    );
  }
}

export const OrderItemValidationRules: EntityValidationRules<OrderItem> = {
  ...ValidSinceValidationRules,
  ...ChargeTypeIdValidationRules,
  ...LicensePlateValidationRules,
  ...LicensePlateConfirmationValidationRules,
  ...CountryIdValidationRules,
  ...FuelTypeValidationRules,
};

export const OrderItemBatchValidationRules: EntityValidationRules<OrderItem> = {
  ...ValidSinceValidationRules,
  ...ChargeTypeIdValidationRules,
  ...CountryIdValidationRules,
  ...FuelTypeValidationRules,
  ...LicensePlatesValidationRules,
};
